import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI, contentAPI, adminAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Loader } from '@/components/ui/loader';

export const AdminContactInfo = () => {
  const navigate = useNavigate();
  const [contact, setContact] = useState({
    phone: '',
    email: '',
    registered_address: { en: '' },
  });
  const [loading, setLoading] = useState(true);

  const fetchContact = useCallback(async () => {
    try {
      const res = await contentAPI.getLLPDetails();
      setContact(res.data);
    } catch (err) {
      console.error('Failed to load contact info', err);
      toast.error('Unable to load contact details');
    } finally {
      setLoading(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      await authAPI.getMe();
      fetchContact();
    } catch (err) {
      navigate('/admin/login');
    }
  }, [navigate, fetchContact]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await adminAPI.updateLLPDetails(contact);
      toast.success(res.data?.message || 'Contact details updated');
      await fetchContact(); // refresh state after save
    } catch (err) {
      console.error('Error updating contact info', err.response || err);
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Failed to update contact details (${status}): ${detail}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader className="text-secondary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 admin-panel">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="ghost" size="sm">
                <ArrowLeft size={18} className="mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-2xl font-heading font-bold">Contact Information</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg shadow-sm">
          <div>
            <label className="block text-sm font-medium mb-2">Email *</label>
            <Input
              type="email"
              value={contact.email}
              onChange={(e) => setContact({ ...contact, email: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Phone *</label>
            <Input
              type="text"
              value={contact.phone}
              onChange={(e) => setContact({ ...contact, phone: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Location *</label>
            <Input
              type="text"
              value={contact.registered_address.en}
              onChange={(e) => setContact({
                ...contact,
                registered_address: { en: e.target.value }
              })}
              required
            />
          </div>

          <Button type="submit" className="w-full">
            Save Changes
          </Button>
        </form>
      </main>
    </div>
  );
};
